// Contratos de los chips de búsqueda del hero.
//
// Estructura:
//   - ChipLabel*: contenido textual trilingüe.
//   - ChipUrl: validación del destino del chip (ruta interna del sitio
//     o URL absoluta externa).
//   - SearchChipPublic*: DTO público (un solo label string, ya traducido
//     al locale solicitado).
//   - SearchChipAdminItem*: DTO admin (label completo + _etag).
//   - Inputs del admin (Create, Update, Reorder).
import { z } from 'zod';
import { IdSchema } from './primitives.js';
import { LOCALES } from './i18n/locales.js';

/**
 * Label del chip: cada locale es opcional, al menos una traducción
 * obligatoria. Sin `default` ni cascada — un chip sin traducción al
 * locale del visitante no se muestra.
 */
export const ChipLabelSchema = z
  .object({
    es: z.string().min(1).optional(),
    ca: z.string().min(1).optional(),
    en: z.string().min(1).optional(),
  })
  .refine((v) => LOCALES.some((l) => typeof v[l] === 'string'), {
    message: 'At least one locale translation is required (es, ca or en)',
  });

/**
 * Un único destino de chip. Acepta dos formas:
 *   - **Ruta interna** del propio sitio: empieza por `/` (pero no por
 *     `//`, que sería protocol-relative) y va **sin** prefijo de locale
 *     — la web antepone el locale activo al renderizar.
 *   - **URL absoluta externa**: `http://` o `https://`, se usa tal cual.
 *
 * Rechaza esquemas peligrosos (`javascript:`, `data:`, …) y las rutas
 * protocol-relative (`//host`).
 */
export const ChipUrlValueSchema = z
  .string()
  .trim()
  .min(1, 'url requerida')
  .superRefine((value, ctx) => {
    // URL absoluta externa: validamos que sea http(s) bien formada.
    if (/^https?:\/\//i.test(value)) {
      try {
        new URL(value);
      } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'URL absoluta no válida' });
      }
      return;
    }
    // Ruta interna del sitio: debe empezar por `/` y no ser
    // protocol-relative (`//host`).
    if (value.startsWith('/') && !value.startsWith('//')) return;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'Debe ser una ruta interna que empiece por "/" o una URL absoluta http(s)://',
    });
  });

/**
 * Destino del chip por idioma. `es` es obligatorio (actúa de fallback
 * cuando el visitante usa un locale sin URL propia); `ca` y `en` son
 * opcionales. Cada valor se valida con {@link ChipUrlValueSchema}.
 * Permite que un mismo chip apunte a rutas con slug traducido
 * (`/eventos` vs `/esdeveniments`) o incluso a destinos distintos por
 * idioma.
 */
export const ChipUrlSchema = z.object({
  es: ChipUrlValueSchema,
  ca: ChipUrlValueSchema.optional(),
  en: ChipUrlValueSchema.optional(),
});

/**
 * Query del endpoint público. `locale` obligatorio: la API filtra y
 * proyecta el label ya traducido para ese idioma.
 */
export const SearchChipsPublicQuerySchema = z.object({
  locale: z.enum(LOCALES),
});

/**
 * DTO público. `url` es el destino ya proyectado al locale solicitado
 * (`url[locale] ?? url.es`): una ruta interna (la web le antepone el
 * locale activo) o una URL absoluta externa (verbatim).
 */
export const SearchChipPublicSchema = z.object({
  id: IdSchema,
  label: z.string().min(1),
  url: z.string().min(1),
  position: z.number().int().nonnegative(),
});

export const SearchChipsPublicResponseSchema = z.object({
  items: z.array(SearchChipPublicSchema),
});

/**
 * Item del chip que recibe el admin: documento completo + `_etag`
 * (para el `If-Match` de update/delete).
 */
export const SearchChipAdminItemSchema = z.object({
  id: IdSchema,
  scope: z.literal('hero'),
  label: ChipLabelSchema,
  url: ChipUrlSchema,
  position: z.number().int().nonnegative(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  _etag: z.string().optional(),
});

export const SearchChipsAdminListResponseSchema = z.object({
  items: z.array(SearchChipAdminItemSchema),
});

export const CreateSearchChipInputSchema = z.object({
  label: ChipLabelSchema,
  url: ChipUrlSchema,
  position: z.number().int().nonnegative().optional(),
  isActive: z.boolean().optional().default(true),
});

export const UpdateSearchChipInputSchema = z
  .object({
    label: ChipLabelSchema.optional(),
    url: ChipUrlSchema.optional(),
    position: z.number().int().nonnegative().optional(),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field must be provided',
  });

export const ReorderSearchChipsInputSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().min(1),
        position: z.number().int().nonnegative(),
      }),
    )
    .min(1),
});

export type ChipLabel = z.infer<typeof ChipLabelSchema>;
export type ChipUrlValue = z.infer<typeof ChipUrlValueSchema>;
export type ChipUrl = z.infer<typeof ChipUrlSchema>;
export type SearchChipPublic = z.infer<typeof SearchChipPublicSchema>;
export type SearchChipsPublicQuery = z.infer<typeof SearchChipsPublicQuerySchema>;
export type SearchChipsPublicResponse = z.infer<typeof SearchChipsPublicResponseSchema>;
export type SearchChipAdminItem = z.infer<typeof SearchChipAdminItemSchema>;
export type SearchChipsAdminListResponse = z.infer<typeof SearchChipsAdminListResponseSchema>;
export type CreateSearchChipInput = z.infer<typeof CreateSearchChipInputSchema>;
export type UpdateSearchChipInput = z.infer<typeof UpdateSearchChipInputSchema>;
export type ReorderSearchChipsInput = z.infer<typeof ReorderSearchChipsInputSchema>;
